import React, { useState, useEffect, useRef } from 'react';
import { db, storage, functions } from '../../firebase/config';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions'; // x3slug-7a4c2e91
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { UploadCloud, Link } from 'lucide-react';
import { countries, states } from '../../utils/data';
import { formatPhoneNumber } from '../../utils/formatters';
import { DOC_TYPES, QUOTE_VALIDITY_PRESETS, DEFAULT_QUOTE_VALIDITY_DAYS, resolveQuoteValidityDays,
    // qexp-6f2a9d38-settings — Seam 5: expiry warning threshold
    EXPIRY_WARNING_PRESETS, DEFAULT_EXPIRY_WARNING_DAYS, resolveExpiryWarningDays,
} from '../../utils/quoteSchema'; // r1-p1-docterm-settings-import-4a9f2e77 qvalid-7c2e4a91
import { getArrivalWindow } from '../../utils/arrivalWindows'; // aw-seam5-cs-import
import { isTaxCodesOn } from '../../utils/taxCodes'; // taxcodes-8d4f1a6e
import { useTaxCodesDoc } from '../../utils/taxExemptionUi'; // taxpick4-54573069

// aw-seam5 — arrival-window editor helpers (all pure, module-level)
const HOURS_12 = [1,2,3,4,5,6,7,8,9,10,11,12];
const MINUTES  = [0, 15, 30, 45];
const WINDOW_KEYS = ['morning', 'afternoon', 'allday'];

const to12h = (h24) => ({ h12: (h24 % 12) || 12, ampm: h24 >= 12 ? 'PM' : 'AM' }); // stored 24h -> display (0->12AM, 12->12PM)
const to24h = (h12, ampm) => (ampm === 'PM' ? (h12 % 12) + 12 : (h12 % 12));        // display -> stored 24h (12AM->0, 12PM->12)

const windowError = (w) => {
  if (!w.label || !w.label.trim()) return 'Name is required.';
  if (w.startH === w.endH && w.startM === w.endM) return 'Start and end cannot be the same time.';
  return ''; // end < start is allowed (overnight) — no error
};
const isOvernightWindow = (w) => (w.endH * 60 + w.endM) < (w.startH * 60 + w.startM);

// A valid default window for a key (unfiltered resolver, never throws).
const buildDefaultWindow = (key, settings) => {
  const w = getArrivalWindow(key, settings);
  return { key, label: w.label, startH: w.startH, startM: w.startM, endH: w.endH, endM: w.endM, enabled: true };
};

// The 3-window working set: raw stored entry per key (preserves in-progress edits,
// including transiently-invalid ones so their errors show), else a valid default.
// Always exactly three, in canonical order.
const getRenderWindows = (settings) => {
  const raw = Array.isArray(settings.arrivalWindows) ? settings.arrivalWindows : null;
  return WINDOW_KEYS.map((key) => {
    const e = raw && raw.find(w => w && w.key === key);
    if (e) return { key, label: e.label ?? '', startH: e.startH, startM: e.startM, endH: e.endH, endM: e.endM, enabled: e.enabled !== false };
    return buildDefaultWindow(key, settings);
  });
};

const INITIAL_COMPANY_SETTINGS = {
    companyName: '', address1: '', address2: '', city: '', state: '',
    zip: '', country: 'US', email: '', accountingEmail: '', phone: '', fax: '', logoUrl: '', // orgemail-0d4e6b91
    taxRate: '', taxId: '',
    paymentTerms: 30, // SEAM 2 — invoice due terms in days (0 = due on receipt)
    quoteEmailGreeting: '', quoteEmailBody: '', quoteEmailFarewell: '',
    currency: 'CAD',
    googleReviewUrl: '',
    slug: '',
    timezone: 'America/Toronto',
    quoteTerminologyDefault: DOC_TYPES.QUOTE, // r1-p1-docterm-settings-default-2f8a6d13
};

// c1-ownedfields-7b3e9d24 — the ONLY plain fields Save writes. The form used to
// write back the whole org doc it had loaded (`...settings`), which echoed server-
// owned fields (Stripe ids, QuickBooks tokens, number counters, ownerUid) and, after
// a God mode switch, copied another org's Stripe account onto this one (BACKLOG
// Finding A). taxRate, paymentTerms, quoteDefaults.validityDays and arrivalWindows
// are also owned, but are normalized separately in handleSave.
const COMPANY_FORM_FIELDS = [
    'companyName', 'address1', 'address2', 'city', 'state', 'zip', 'country',
    'email', 'accountingEmail', 'phone', 'fax', 'logoUrl', 'taxId',
    'quoteEmailGreeting', 'quoteEmailBody', 'quoteEmailFarewell',
    'currency', 'googleReviewUrl', 'timezone', 'quoteTerminologyDefault',
];
// x3slug-7a4c2e91 — `slug` (the booking page address) is NOT saved here any more. It is server-owned
// (security audit 2026-10-15 #3): firestore.rules refuses a client change, and the address is set
// through the setBookingPageAddress callable (its own button below), which refuses one already taken.

const CompanySettings = ({ user, currentUserProfile, onOpenTaxes }) => {
    // taxcodes-8d4f1a6e — where Settings › Taxes is switched on, the tax rate is set there (saveTaxCodes
    // copies the default's total into taxRate), so this page neither shows nor saves taxRate.
    // taxpick4-54573069 — the switch is read from settings/taxCodes (missing or still loading = the legacy list).
    const taxCodesDoc = useTaxCodesDoc(currentUserProfile?.organizationId);
    const taxCodesOn = isTaxCodesOn(currentUserProfile?.organizationId, taxCodesDoc === undefined ? null : taxCodesDoc);
    const [settings, setSettings] = useState(INITIAL_COMPANY_SETTINGS);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [slugError, setSlugError] = useState('');
    // x3slug-7a4c2e91 — the address as saved on the server (the field above it is the draft).
    const [savedSlug, setSavedSlug] = useState('');
    const [isSavingSlug, setIsSavingSlug] = useState(false);
    const [slugMessage, setSlugMessage] = useState({ text: '', type: '' });
    // SEAM 2 — forces the terms dropdown into "Custom" mode (for non-preset days).
    const [termsCustomMode, setTermsCustomMode] = useState(false);
    const fileInputRef = useRef(null);

    const highLevelRoles = ['Admin', 'Manager', 'Owner/System Admin', 'Owner / System Admin', 'Owner', 'System Admin'];
    const isAdmin = highLevelRoles.includes(currentUserProfile?.role);

    const currencyOptions = [
        { code: 'CAD', name: 'Canadian Dollar' },
        { code: 'USD', name: 'US Dollar' },
        { code: 'EUR', name: 'Euro' },
        { code: 'GBP', name: 'British Pound' },
        { code: 'AUD', name: 'Australian Dollar' },
    ];

    // tzcanada-9b4e2f17 — from 2026-11-01 British Columbia, Alberta, Inuvik and Manitoba no longer
    // change their clocks (IANA tzdata 2026b–e), so their old "PST/PDT"-style labels were wrong.
    // Same list, same order, as the phone's SettingsScreen TIMEZONE_OPTIONS.
    const timezoneOptions = [
        { value: 'America/St_Johns',     label: 'Newfoundland — Newfoundland Time (NST/NDT)' },
        { value: 'America/Halifax',      label: 'Nova Scotia, New Brunswick & PEI — Atlantic Time (AST/ADT)' },
        { value: 'America/Toronto',      label: 'Ontario & Quebec — Eastern Time (EST/EDT)' },
        { value: 'America/Winnipeg',     label: 'Manitoba (UTC−5 all year)' },
        { value: 'America/Regina',       label: 'Saskatchewan (UTC−6 all year)' },
        { value: 'America/Edmonton',     label: 'Alberta (UTC−6 all year)' },
        { value: 'America/Inuvik',       label: 'Northwest Territories — Inuvik (UTC−6 all year)' },
        { value: 'America/Vancouver',    label: 'British Columbia — Pacific Time (UTC−7 all year)' },
        { value: 'America/Whitehorse',   label: 'Yukon (UTC−7 all year)' },
        { value: 'America/New_York',     label: 'Eastern Time US (EST/EDT)' },
        { value: 'America/Chicago',      label: 'Central Time US (CST/CDT)' },
        { value: 'America/Denver',       label: 'Mountain Time US (MST/MDT)' },
        { value: 'America/Los_Angeles',  label: 'Pacific Time US (PST/PDT)' },
        { value: 'America/Phoenix',      label: 'Arizona (MST, no DST)' },
        { value: 'America/Anchorage',    label: 'Alaska Time (AKST/AKDT)' },
        { value: 'Pacific/Honolulu',     label: 'Hawaii Time (HST)' },
    ];

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        // c1-orgswitch-reset-4a8f1c62 — start from defaults whenever the org changes, so
        // nothing loaded from a previously viewed org (God mode switch) survives into this one.
        setSettings(INITIAL_COMPANY_SETTINGS);
        setSlugError('');
        setSavedSlug('');
        setSlugMessage({ text: '', type: '' });
        const settingsRef = doc(db, 'organizations', currentUserProfile.organizationId);
        const unsub = onSnapshot(settingsRef, (doc) => {
            if (doc.exists()) {
                setSettings(prev => ({ ...prev, ...doc.data() }));
                setSavedSlug(doc.data().slug || ''); // x3slug-7a4c2e91
            }
        });
        return unsub;
    }, [currentUserProfile?.organizationId]);

    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => setMessage({ text: '', type: '' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'phone' || name === 'fax') {
            setSettings(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
        } else {
            setSettings(prev => ({ ...prev, [name]: value }));
        }
    };

    // aw-seam5 — patch one arrival window; seeds the full 3-window array into settings
    // on first edit so the existing dataToSave/setDoc merge persists it (no new machinery).
    const updateWindow = (key, patch) => {
        setSettings(prev => {
            const base = getRenderWindows(prev);
            return { ...prev, arrivalWindows: base.map(w => (w.key === key ? { ...w, ...patch } : w)) };
        });
    };

    // Slug field: enforce lowercase alphanumeric + hyphens only
    const handleSlugChange = (e) => {
        const raw = e.target.value;
        const normalized = raw.toLowerCase().replace(/[^a-z0-9-]/g, '');
        setSettings(prev => ({ ...prev, slug: normalized }));

        if (raw !== normalized && raw.length > 0) {
            setSlugError('Only lowercase letters, numbers, and hyphens are allowed.');
        } else if (normalized.startsWith('-') || normalized.endsWith('-')) {
            setSlugError('Slug cannot start or end with a hyphen.');
        } else {
            setSlugError('');
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !currentUserProfile?.organizationId) return;

        setIsUploading(true);
        setMessage({ text: '', type: '' });
        try {
            const storageRef = ref(storage, `organizations/${currentUserProfile.organizationId}/logo`);
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);
            setSettings(prev => ({ ...prev, logoUrl: downloadURL }));
            setMessage({ text: 'Logo uploaded successfully! Click Save to apply.', type: 'success' });
        } catch (error) {
            console.error("Error uploading logo:", error);
            setMessage({ text: 'Failed to upload logo. Please see console for details.', type: 'error' });
        }
        setIsUploading(false);
    };

    // aw-seam5 — derived arrival-window state (defined above handleSave so its closure
    // and the Save button can read it; the section JSX reads the same values).
    const arrivalWindows = getRenderWindows(settings);
    const allWindowsDisabled = arrivalWindows.every(w => !w.enabled);
    const arrivalWindowsInvalid = arrivalWindows.some(w => windowError(w)) || allWindowsDisabled;

    const handleSave = async () => {
        if (!currentUserProfile?.organizationId) return;

        // aw-seam5 — block save while any arrival window is invalid or all are disabled.
        if (arrivalWindowsInvalid) {
            setMessage({ text: 'Please fix the arrival windows before saving.', type: 'error' });
            return;
        }

        setIsSaving(true);
        setMessage({ text: '', type: '' });
        try {
            const settingsRef = doc(db, 'organizations', currentUserProfile.organizationId);
            // SEAM 2 — persist paymentTerms as a non-negative integer (0 = due on
            // receipt); default a blank/invalid entry back to 30.
            const _pt = Math.trunc(Number(settings.paymentTerms));
            // c1-ownedfields-7b3e9d24 — owned fields only, never `...settings`.
            const ownedFields = {};
            COMPANY_FORM_FIELDS.forEach(key => {
                if (settings[key] !== undefined) ownedFields[key] = settings[key];
            });
            const dataToSave = {
                ...ownedFields,
                // taxcodes-8d4f1a6e — never echo a loaded taxRate back over a Taxes save.
                ...(taxCodesOn ? {} : { taxRate: Number(settings.taxRate) || 0 }),
                paymentTerms: (Number.isFinite(_pt) && _pt >= 0) ? _pt : 30,
                // qvalid-settings-save-2d7f4b90 — normalize the estimate validity setting,
                // but ONLY when the org already has one or the picker was touched. An org
                // that never chooses stays unset and keeps reading the code-side fallback.
                // qexp-6f2a9d38-settings — Seam 5 adds expiryWarningDays to the same
                // map. BOTH keys are written together on purpose: setDoc's merge
                // would preserve an omitted one, but writing both makes this save
                // independent of that behaviour, so a future switch to updateDoc
                // (which replaces a map wholesale) can't silently drop a setting.
                ...(settings.quoteDefaults ? {
                    quoteDefaults: {
                        validityDays:      resolveQuoteValidityDays(settings.quoteDefaults),
                        expiryWarningDays: resolveExpiryWarningDays(settings.quoteDefaults),
                    },
                } : {}),
                // aw-seam5 — normalize to the exact shape _isValidWindow/_resolveWindow read.
                arrivalWindows: getRenderWindows(settings).map(w => ({
                    key: w.key,
                    label: (w.label ?? '').trim(),
                    startH: Math.trunc(Number(w.startH)), startM: Math.trunc(Number(w.startM)),
                    endH:   Math.trunc(Number(w.endH)),   endM:   Math.trunc(Number(w.endM)),
                    enabled: w.enabled !== false,
                })),
            };
            await setDoc(settingsRef, dataToSave, { merge: true });
            setMessage({ text: 'Settings saved successfully!', type: 'success' });
        } catch (error) {
            console.error("Error saving settings:", error);
            setMessage({ text: 'Failed to save settings.', type: 'error' });
        }
        setIsSaving(false);
    };

    // x3slug-7a4c2e91 — claim / change / remove the booking page address on the server. The server
    // refuses an address another business already uses; nothing else on this screen is saved here.
    const handleSaveSlug = async () => {
        if (slugError || isSavingSlug) return;
        setIsSavingSlug(true);
        setSlugMessage({ text: '', type: '' });
        try {
            const res = await httpsCallable(functions, 'setBookingPageAddress')({ slug: settings.slug || '' });
            const saved = (res && res.data && res.data.slug) || '';
            setSavedSlug(saved);
            setSettings(prev => ({ ...prev, slug: saved }));
            setSlugMessage({ text: saved ? 'Booking page address saved.' : 'Booking page removed.', type: 'success' });
        } catch (error) {
            console.error('Error saving booking page address:', error);
            setSlugMessage({ text: error.message || 'Could not save the booking page address.', type: 'error' });
        }
        setIsSavingSlug(false);
    };
    const slugDirty = (settings.slug || '') !== (savedSlug || '');

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">Company Information</h2>
            <div className="space-y-4">
                <input name="companyName" value={settings.companyName || ''} onChange={handleChange} placeholder="Company Name" className="w-full p-2 border rounded" />
                <input name="address1" value={settings.address1 || ''} onChange={handleChange} placeholder="Address Line 1" className="w-full p-2 border rounded" />
                <input name="address2" value={settings.address2 || ''} onChange={handleChange} placeholder="Address Line 2" className="w-full p-2 border rounded" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input name="city" value={settings.city || ''} onChange={handleChange} placeholder="City" className="w-full p-2 border rounded" />
                    <input name="zip" value={settings.zip || ''} onChange={handleChange} placeholder="ZIP / Postal Code" className="w-full p-2 border rounded" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <select name="country" value={settings.country || 'US'} onChange={handleChange} className="w-full p-2 border rounded bg-white">
                        {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                    <select name="state" value={settings.state || ''} onChange={handleChange} className="w-full p-2 border rounded bg-white">
                        <option value="">-- Select State/Province --</option>
                        {(states[settings.country] || []).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <input name="email" value={settings.email || ''} onChange={handleChange} placeholder="Company Email" className="w-full p-2 border rounded" />
                {/* orgemail-9c2a1f58 — non-blocking nudge when no org email is set */}
                {!settings.email && (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                        No company email set. Until you add one, customers on your portal’s Contact page will see the account owner’s email address.
                    </p>
                )}
                {/* orgemail-5a3f8c26 — accounting/billing reply-to inbox */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Accounting Email <span className="font-normal text-gray-500">(optional)</span></label>
                    <input name="accountingEmail" value={settings.accountingEmail || ''} onChange={handleChange} placeholder="e.g. billing@yourcompany.com" className="mt-1 w-full p-2 border rounded" />
                    <p className="text-xs text-gray-500 mt-1">Replies to automated invoice and payment-reminder emails go here. Leave blank to use your Company Email.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input type="tel" name="phone" value={settings.phone || ''} onChange={handleChange} placeholder="Phone Number" className="w-full p-2 border rounded" />
                    <input type="tel" name="fax" value={settings.fax || ''} onChange={handleChange} placeholder="Fax Number" className="w-full p-2 border rounded" />
                </div>
                
                {taxCodesOn ? (
                    <div className="p-3 rounded-lg border border-[#5F4EC9]/30 bg-[#5F4EC9]/5 text-sm">
                        <p className="font-medium text-gray-800">Taxes</p>
                        <p className="text-gray-600 mt-0.5">Your tax rates are now set up in Settings → Taxes.</p>
                        {onOpenTaxes && (
                            <button type="button" onClick={onOpenTaxes} className="mt-2 text-sm font-semibold text-[#5F4EC9] hover:underline">Go to Taxes →</button>
                        )}
                    </div>
                ) : (
                <div>
                    <label className="block text-sm font-medium text-gray-700">Tax Rate (%)</label>
                    <input type="number" name="taxRate" value={settings.taxRate || ''} onChange={handleChange} placeholder="e.g. 8.5" className="mt-1 w-full p-2 border rounded" />
                </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700">Tax ID / GST / VAT Number</label>
                    <input 
                        name="taxId" 
                        value={settings.taxId || ''} 
                        onChange={handleChange} 
                        placeholder="e.g. 123456789 RT0001" 
                        className="mt-1 w-full p-2 border rounded" 
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Default Currency</label>
                    <select name="currency" value={settings.currency || 'CAD'} onChange={handleChange} className="mt-1 w-full p-2 border rounded bg-white">
                        {currencyOptions.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                    </select>
                </div>

                {/* SEAM 2 — invoice payment terms (0 = due on receipt) */}
                {(() => {
                    const ptNum = Number(settings.paymentTerms ?? 30);
                    const showCustom = termsCustomMode || ![0, 15, 30, 60].includes(ptNum);
                    return (
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Invoice Payment Terms</label>
                            <select
                                value={showCustom ? 'custom' : String(ptNum)}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === 'custom') setTermsCustomMode(true);
                                    else { setTermsCustomMode(false); setSettings(prev => ({ ...prev, paymentTerms: Number(v) })); }
                                }}
                                className="mt-1 w-full p-2 border rounded bg-white"
                            >
                                <option value="0">Due on receipt</option>
                                <option value="15">Net 15</option>
                                <option value="30">Net 30</option>
                                <option value="60">Net 60</option>
                                <option value="custom">Custom…</option>
                            </select>
                            {showCustom && (
                                <input
                                    type="number" min="0" step="1"
                                    value={settings.paymentTerms ?? ''}
                                    onChange={(e) => setSettings(prev => ({ ...prev, paymentTerms: e.target.value === '' ? '' : Math.max(0, Math.trunc(Number(e.target.value))) }))}
                                    placeholder="Days until due (0 = due on receipt)"
                                    className="mt-2 w-full p-2 border rounded"
                                />
                            )}
                            <p className="mt-1 text-xs text-gray-400">Default due date on new invoices: invoice date + this many days. 0 = due on receipt.</p>
                        </div>
                    );
                })()}

                <div>
                    <label className="block text-sm font-medium text-gray-700">Timezone</label>
                    {/* e5f6a7b8 — tolerate a stored IANA zone not in timezoneOptions (e.g. one
                        detected from the browser at signup, like America/Regina). Render it as a
                        leading option so it displays and round-trips on save instead of showing
                        blank — which would otherwise invite an owner to overwrite a correct zone. */}
                    <select name="timezone" value={settings.timezone || 'America/Toronto'} onChange={handleChange} className="mt-1 w-full p-2 border rounded bg-white">
                        {settings.timezone && !timezoneOptions.some(tz => tz.value === settings.timezone) && (
                            <option value={settings.timezone}>{settings.timezone}</option>
                        )}
                        {timezoneOptions.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                    </select>
                </div>

                <div className="flex items-center gap-4">
                    {settings.logoUrl && <img src={settings.logoUrl} alt="Company Logo" className="h-16 w-16 object-contain rounded-md border" />}
                    <input type="file" ref={fileInputRef} onChange={handleLogoUpload} className="hidden" accept="image/*" />
                    <button onClick={() => fileInputRef.current.click()} disabled={isUploading} className="flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 disabled:bg-gray-300">
                        <UploadCloud size={16} />
                        {isUploading ? 'Uploading...' : 'Upload Logo'}
                    </button>
                </div>

                {/* Quote Email Template */}
                <div className="pt-4 mt-4 border-t">
                    <h3 className="text-lg font-semibold mb-2">Quote Email Template</h3>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Greeting</label>
                        <input name="quoteEmailGreeting" value={settings.quoteEmailGreeting || ''} onChange={handleChange} placeholder="e.g., Dear {Customer Name}," className="mt-1 w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mt-2">Body</label>
                        <textarea name="quoteEmailBody" value={settings.quoteEmailBody || ''} onChange={handleChange} placeholder="e.g., Thank you for your interest..." rows="3" className="mt-1 w-full p-2 border rounded"></textarea>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mt-2">Farewell</label>
                        <input name="quoteEmailFarewell" value={settings.quoteEmailFarewell || ''} onChange={handleChange} placeholder="e.g., Best regards," className="mt-1 w-full p-2 border rounded" />
                    </div>
                </div>

                {/* Google Reviews */}
                <div className="pt-4 mt-4 border-t">
                    <h3 className="text-lg font-semibold mb-1">Google Reviews</h3>
                    <p className="text-sm text-gray-500 mb-3">
                        Paste your Google Business Profile review link here. Staff can send a one-click review request to customers directly from their profile.{' '}
                        <a
                            href="https://support.google.com/business/answer/7035772"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                        >
                            How to find your review link ↗
                        </a>
                    </p>
                    <input
                        name="googleReviewUrl"
                        value={settings.googleReviewUrl || ''}
                        onChange={handleChange}
                        placeholder="https://g.page/r/your-business/review"
                        className="w-full p-2 border rounded text-sm"
                    />
                </div>

                {/* ---- DOCUMENT TYPE DEFAULT (Admin only) --- r1-p1-docterm-settings-select-7e3c9b41 ---- */}
                {isAdmin && (
                    <div className="pt-4 mt-4 border-t">
                        <h3 className="text-lg font-semibold mb-1">Quotes &amp; Estimates</h3>
                        <p className="text-sm text-gray-500 mb-3">
                            Choose the default label for new quotes and estimates. Existing documents keep their original label.
                        </p>
                        <select name="quoteTerminologyDefault" value={settings.quoteTerminologyDefault || DOC_TYPES.QUOTE} onChange={handleChange} className="mt-1 w-full p-2 border rounded bg-white">
                            <option value={DOC_TYPES.QUOTE}>Quote</option>
                            <option value={DOC_TYPES.ESTIMATE}>Estimate</option>
                        </select>

                        {/* qvalid-settings-select-6c3a8e15 — Seam 2: default validity for NEW documents.
                            Aggregate/org surface, so the copy stays neutral ("quotes and estimates"). */}
                        <label className="block text-sm font-medium text-gray-700 mt-4">Default validity for new quotes and estimates</label>
                        <select
                            value={String(resolveQuoteValidityDays(settings.quoteDefaults))}
                            onChange={(e) => setSettings(prev => ({ ...prev, quoteDefaults: { ...(prev.quoteDefaults || {}), validityDays: Number(e.target.value) } }))}
                            className="mt-1 w-full p-2 border rounded bg-white"
                        >
                            {QUOTE_VALIDITY_PRESETS.map(d => (
                                <option key={d} value={String(d)}>
                                    {d} days{d === 14 ? ' (common for contractors)' : ''}{d === DEFAULT_QUOTE_VALIDITY_DAYS ? ' (default)' : ''}
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-400">
                            New quotes and estimates expire this many days after they're created, and the date is shown to your customer on the email and PDF. Existing documents keep their dates.
                        </p>

                        {/* qexp-6f2a9d38-settings — Seam 5: how early the warning starts.
                            Deliberately beside the validity picker — the two numbers only
                            make sense read together. */}
                        <label className="block text-sm font-medium text-gray-700 mt-4">Warn me this many days before one expires</label>
                        <select
                            value={String(resolveExpiryWarningDays(settings.quoteDefaults))}
                            onChange={(e) => setSettings(prev => ({ ...prev, quoteDefaults: { ...(prev.quoteDefaults || {}), expiryWarningDays: Number(e.target.value) } }))}
                            className="mt-1 w-full p-2 border rounded bg-white"
                        >
                            {EXPIRY_WARNING_PRESETS.map(d => (
                                <option key={d} value={String(d)}>
                                    {d} days{d === DEFAULT_EXPIRY_WARNING_DAYS ? ' (default)' : ''}
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-400">
                            A quote or estimate this close to its expiry date is flagged "Expiring soon" in your list, so you can chase it before the price lapses rather than after.
                        </p>
                    </div>
                )}

                {/* ---- ARRIVAL WINDOWS (Admin only) --- aw-seam5-settings-section ---- */}
                {isAdmin && (
                    <div className="pt-4 mt-4 border-t">
                        <h3 className="text-lg font-semibold mb-1">Arrival Windows</h3>
                        <p className="text-sm text-gray-500 mb-3">
                            Name the arrival windows customers and staff see, set their times, and turn any
                            of the three on or off. Turning a window off hides it from the job form; it never
                            changes jobs already scheduled in it.
                        </p>

                        {arrivalWindows.map((w) => {
                            const err = windowError(w);
                            const overnight = isOvernightWindow(w);
                            const start = to12h(w.startH);
                            const end   = to12h(w.endH);
                            return (
                                <div key={w.key} className="p-4 bg-white border border-gray-200 rounded-xl mb-3">
                                    {/* Row 1: name + enable toggle (toggle markup = TechPermissionsSettings) */}
                                    <div className="flex items-center justify-between gap-4">
                                        <input
                                            type="text"
                                            value={w.label}
                                            onChange={(e) => updateWindow(w.key, { label: e.target.value })}
                                            placeholder="Window name"
                                            className="mt-1 w-full p-2 border rounded"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => updateWindow(w.key, { enabled: !w.enabled })}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${w.enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${w.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    </div>

                                    {/* Row 2: start / end selects (hour · minute · AM/PM) */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Start</label>
                                            <div className="flex items-center gap-2 mt-1">
                                                <select value={start.h12} onChange={(e) => updateWindow(w.key, { startH: to24h(Number(e.target.value), start.ampm) })} className="p-2 border rounded bg-white">
                                                    {HOURS_12.map(h => <option key={h} value={h}>{h}</option>)}
                                                </select>
                                                <select value={w.startM} onChange={(e) => updateWindow(w.key, { startM: Number(e.target.value) })} className="p-2 border rounded bg-white">
                                                    {MINUTES.map(m => <option key={m} value={m}>:{String(m).padStart(2,'0')}</option>)}
                                                </select>
                                                <select value={start.ampm} onChange={(e) => updateWindow(w.key, { startH: to24h(start.h12, e.target.value) })} className="p-2 border rounded bg-white">
                                                    <option value="AM">AM</option><option value="PM">PM</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">End</label>
                                            <div className="flex items-center gap-2 mt-1">
                                                <select value={end.h12} onChange={(e) => updateWindow(w.key, { endH: to24h(Number(e.target.value), end.ampm) })} className="p-2 border rounded bg-white">
                                                    {HOURS_12.map(h => <option key={h} value={h}>{h}</option>)}
                                                </select>
                                                <select value={w.endM} onChange={(e) => updateWindow(w.key, { endM: Number(e.target.value) })} className="p-2 border rounded bg-white">
                                                    {MINUTES.map(m => <option key={m} value={m}>:{String(m).padStart(2,'0')}</option>)}
                                                </select>
                                                <select value={end.ampm} onChange={(e) => updateWindow(w.key, { endH: to24h(end.h12, e.target.value) })} className="p-2 border rounded bg-white">
                                                    <option value="AM">AM</option><option value="PM">PM</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Overnight helper — muted, only when overnight and no error */}
                                    {overnight && !err && (
                                        <p className="mt-1 text-xs text-gray-400">Runs overnight — ends the next day</p>
                                    )}
                                    {/* Inline validation — slug-error styling */}
                                    {err && (<p className="text-xs text-red-600 mt-1">{err}</p>)}
                                </div>
                            );
                        })}

                        {allWindowsDisabled && (
                            <p className="text-xs text-red-600 mt-1">At least one window must be enabled.</p>
                        )}
                    </div>
                )}

                {/* ---- BOOKING PAGE URL (Admin only) ---- */}
                {isAdmin && (
                    <div className="pt-4 mt-4 border-t">
                        <h3 className="text-lg font-semibold mb-1">Online Booking Page</h3>
                        <p className="text-sm text-gray-500 mb-3">
                            Set a unique URL for your public booking page. Customers can submit service requests directly through this link. Only lowercase letters, numbers, and hyphens are allowed.
                        </p>
                        <div className="flex items-center border rounded overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
                            <span className="px-3 py-2 bg-gray-100 text-gray-500 text-sm border-r select-none whitespace-nowrap">
                                spatchy.co/book/
                            </span>
                            <input
                                name="slug"
                                value={settings.slug || ''}
                                onChange={handleSlugChange}
                                placeholder="your-business-name"
                                className="flex-1 px-3 py-2 text-sm focus:outline-none"
                            />
                        </div>
                        {slugError && (
                            <p className="text-xs text-red-600 mt-1">{slugError}</p>
                        )}
                        {/* x3slug-7a4c2e91 — its own Save: the address is checked against every business on the server */}
                        <div className="mt-2 flex items-center gap-3">
                            <button type="button" onClick={handleSaveSlug} disabled={!slugDirty || !!slugError || isSavingSlug}
                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300">
                                {isSavingSlug ? 'Saving...' : (settings.slug ? 'Save booking page address' : 'Remove booking page')}
                            </button>
                            {slugDirty && !isSavingSlug && <span className="text-xs text-gray-500">Not saved yet</span>}
                        </div>
                        {slugMessage.text && (
                            <p className={`text-xs mt-1 ${slugMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{slugMessage.text}</p>
                        )}
                        {savedSlug && !slugDirty && !slugError && (
                            <div className="mt-2 flex items-center gap-2">
                                <Link size={13} className="text-blue-500 flex-shrink-0" />
                                <a
                                    href={`https://spatchy.co/book/${savedSlug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline break-all"
                                >
                                    spatchy.co/book/{savedSlug}
                                </a>
                            </div>
                        )}
                        <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                            ⚠️ Each address can belong to only one business. If another business already uses it, you will be asked to choose a different one.
                        </p>
                    </div>
                )}

                {message.text && (
                    <p className={`text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {message.text}
                    </p>
                )}

                <button onClick={handleSave} disabled={isSaving || isUploading || arrivalWindowsInvalid} className="w-full mt-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"> {/* aw-seam5 */}
                    {isSaving ? 'Saving...' : 'Save Company Settings'}
                </button>
            </div>
        </div>
    );
};

export default CompanySettings;